import "./ProyectosCommon.css";
import { BasePageComponent } from "../../../shared/components/pages/BasePageComponent";
import { DashboardStatsResponse } from "../../../shared/response/proyectos/DashboardStatsResponse";
import { ProyectosDashboardService } from "../../services/proyectos/ProyectosDashboardService";
import { ProyectoService } from "../../services/proyectos/ProyectoService";
import { ProyectoResponse } from "../../../shared/response/proyectos/ProyectoResponse";
import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";
import { fmtCentavos, fmtPct, fmtFechaProy } from "../../../shared/utils/proyectosFormat";
import { formatKg } from "../../../shared/utils/formatUtils";
import { downloadCsv } from "../../../shared/utils/csvUtils";
import { proyectoDeepLink } from "./ProyectosPage";
import { cultivoEmoji } from "../../../shared/utils/cultivoEmoji";

const CULTIVO_COLORS = ["#1d9e75", "#378ADD", "#BA7517", "#8B5CF6", "#E24B4A", "#0EA5E9"];

const ESTADO_LABELS: Record<string, string> = {
  borrador: "Borrador", en_revision: "En revisión", aprobado: "Aprobado",
  activo: "Activo", cerrado: "Cerrado", suspendido: "Suspendido",
};
const ESTADO_ORDEN = ["activo", "en_revision", "aprobado", "borrador", "suspendido", "cerrado"];
const ESTADO_COLORS: Record<string, string> = {
  activo: "var(--sx-success)", en_revision: "var(--sx-warning)", aprobado: "var(--sx-info)",
  borrador: "var(--sx-text-tertiary)", suspendido: "var(--sx-error)", cerrado: "var(--sx-text-tertiary)",
};

export class ProyectosDashboardPage extends BasePageComponent {
  private _content!: HTMLElement;
  private _proyectos: ProyectoResponse[] = [];
  private _stats: DashboardStatsResponse | null = null;

  public getAppRoute(): string { return "/proyectos/dashboard"; }
  public getPageTitle(): string | null { return "Proyectos"; }

  protected override renderPageContent(pageContent: HTMLDivElement): void {
    pageContent.className = "sx-page pr-page";
    pageContent.innerHTML = `
      <div class="pr-content">
        <div class="pr-pg-hd">
          <div>
            <div class="pr-pg-ey">Proyectos</div>
            <div class="pr-pg-ti">Dashboard</div>
            <div class="pr-pg-su">Resumen de proyectos activos, ejecución CAPEX/OPEX y desviaciones</div>
          </div>
          <button class="sx-btn sx-btn-secondary sx-btn-sm" id="pr-dash-exportar"><i class="ph ph-download-simple"></i> Exportar</button>
        </div>
        <div id="pr-dash"><div class="pr-empty">Cargando…</div></div>
      </div>
    `;
    this._content = document.getElementById("pr-dash")!;
    document.getElementById("pr-dash-exportar")!.onclick = () => this._onExportar();
    void this._loadData();
  }

  protected override beforeDispose(): void {}

  private async _loadData(): Promise<void> {
    try {
      const [s, proyectos] = await Promise.all([
        ProyectosDashboardService.instance.getStats(),
        ProyectoService.instance.get().catch(() => [] as ProyectoResponse[]),
      ]);
      this._proyectos = proyectos.filter((p) => !p.isEliminado);
      this._render(s);
    } catch (error) {
      errorMessageComponent.addError("Error al cargar el dashboard", (error as Error).message);
      this._content.innerHTML = `<div class="pr-empty">Error al cargar</div>`;
    }
  }

  private _render(s: DashboardStatsResponse): void {
    this._stats = s;
    s.haPorCultivo = s.haPorCultivo ?? [];
    s.mayorDesviacion = s.mayorDesviacion ?? [];
    s.proyectosPorEstado = s.proyectosPorEstado ?? [];
    const capexPct = s.capexPresupuestado > 0 ? Math.round((s.capexEjecutado / s.capexPresupuestado) * 100) : 0;
    const opexPct  = s.opexPresupuestado > 0 ? Math.round((s.opexEjecutado / s.opexPresupuestado) * 100) : 0;
    const maxHa = Math.max(...s.haPorCultivo.map((h) => h.totalHa), 1);
    const totalHaCult = s.haPorCultivo.reduce((a, h) => a + h.totalHa, 0) || 1;
    const totalProyectos = s.proyectosPorEstado.reduce((a, e) => a + e.cantidad, 0) || 1;
    const maxEstado = Math.max(...s.proyectosPorEstado.map((e) => e.cantidad), 1);

    this._content.innerHTML = `
      ${this._alertaHtml(s)}

      <div class="pr-kstrip" style="grid-template-columns:repeat(auto-fit,minmax(130px,1fr))">
        ${this._kpi(String(s.macroProyectosActivos), "MACRO-PROYECTOS", "", `${s.totalMacroProyectos} en total`)}
        ${this._kpi(String(s.totalNucleos), "NÚCLEOS", "", "")}
        ${this._kpi(String(s.proyectosActivos), "PROYECTOS ACTIVOS", "", s.nuevosEsteMes > 0 ? `+${s.nuevosEsteMes} este mes` : "")}
        ${this._kpi(String(s.totalBeneficiarios), "BENEFICIARIOS", "", "con proyectos")}
        ${this._kpi(formatKg(s.totalHa) + " ha", "HA TOTALES", "", "")}
        ${this._kpi(String(s.tecnicosActivos), "TÉCNICOS ACTIVOS", "blue", "")}
        ${this._kpi(fmtCentavos(s.capexPresupuestado), "CAPEX PRESUPUESTADO", "pr-k-val-money", `Ejec. ${fmtCentavos(s.capexEjecutado)} · ${capexPct}%`)}
        ${this._kpi(fmtCentavos(s.opexPresupuestado), "OPEX PRESUPUESTADO", "pr-k-val-money", `Ejec. ${fmtCentavos(s.opexEjecutado)} · ${opexPct}%`)}
        ${this._kpi(s.snapshotAño != null ? String(s.snapshotAño) : "—", "SNAPSHOT VIGENTE", "", s.snapshotIpc != null ? "IPC " + fmtPct(s.snapshotIpc) : "Sin snapshot")}
      </div>

      <div class="pr-row3">
        <div class="pr-panel">
          <div class="pr-panel-t">Proyectos por estado <span style="font-weight:var(--sx-weight-regular);color:var(--sx-text-tertiary)">· ${totalProyectos} total</span></div>
          ${s.proyectosPorEstado.length === 0 ? `<div class="pr-empty">Sin datos</div>` : ESTADO_ORDEN
            .map((estado) => s.proyectosPorEstado.find((e) => e.estado === estado))
            .filter((e): e is NonNullable<typeof e> => !!e && e.cantidad > 0)
            .map((e) => `
              <div class="pr-bar-row">
                <span class="pr-bar-lbl">${ESTADO_LABELS[e.estado] ?? e.estado}</span>
                <div class="pr-bar-track"><div class="pr-bar-fill" style="width:${Math.round(e.cantidad / maxEstado * 100)}%;background:${ESTADO_COLORS[e.estado] ?? "var(--sx-text-tertiary)"}"></div></div>
                <span class="pr-bar-val">${e.cantidad} · ${Math.round(e.cantidad / totalProyectos * 100)}%</span>
              </div>`).join("")}
        </div>

        <div class="pr-panel">
          <div class="pr-panel-t">Ha por cultivo</div>
          ${s.haPorCultivo.length === 0 ? `<div class="pr-empty">Sin datos</div>` : s.haPorCultivo.map((h, i) => `
            <div class="pr-bar-row">
              <span class="pr-bar-lbl"><span class="pr-cultivo-emoji" aria-hidden="true">${cultivoEmoji(h.cultivoNombre)}</span>${this._esc(h.cultivoNombre)}</span>
              <div class="pr-bar-track"><div class="pr-bar-fill" style="width:${Math.round(h.totalHa / maxHa * 100)}%;background:${CULTIVO_COLORS[i % CULTIVO_COLORS.length]}"></div></div>
              <span class="pr-bar-val">${formatKg(h.totalHa)} ha · ${Math.round(h.totalHa / totalHaCult * 100)}%</span>
            </div>`).join("")}
        </div>

        <div class="pr-panel">
          <div class="pr-panel-t">Mayor desviación de costo</div>
          ${s.mayorDesviacion.length === 0 ? `<div class="pr-empty">Sin desviaciones</div>` : s.mayorDesviacion.map((d) => `
            <div class="pr-desv-row">
              <div>
                <div class="pr-desv-name">${this._esc(d.itemNombre)}</div>
                <div class="pr-desv-sub">${this._esc(d.cultivoNombre)} · ${d.proyectosAfectados} proyecto${d.proyectosAfectados === 1 ? "" : "s"}</div>
              </div>
              <span class="${d.desviacion >= 0 ? "pr-desv-pos" : "pr-desv-neg"}">${d.desviacion >= 0 ? "+" : ""}${fmtPct(d.desviacion, 0)}</span>
            </div>`).join("")}
        </div>

        ${this._donutHtml(s)}
      </div>

      ${this._proyectosTablaHtml()}
    `;

    document.getElementById("pr-dash-revisar")?.addEventListener("click", () => this.navigate("/proyectos/proyectos"));
    this._content.querySelectorAll<HTMLElement>("[data-proyecto-id]").forEach((row) => {
      row.onclick = () => {
        proyectoDeepLink.pendingId = row.dataset.proyectoId!;
        this.navigate("/proyectos/proyectos");
      };
    });
  }

  private _alertaHtml(s: DashboardStatsResponse): string {
    if (s.proyectosPendientesAprobacion === 0) return "";
    const detalleDias = s.proyectosPendientesMasDe5Dias > 0
      ? ` — ${s.proyectosPendientesMasDe5Dias} llevan más de 5 días sin revisión`
      : "";
    return `
      <div class="pr-alert-warn" style="margin-bottom:16px;display:flex;align-items:center;gap:10px">
        <i class="ph ph-warning-circle"></i>
        <span><strong>${s.proyectosPendientesAprobacion} proyecto${s.proyectosPendientesAprobacion === 1 ? "" : "s"} pendiente${s.proyectosPendientesAprobacion === 1 ? "" : "s"} de aprobación</strong>${detalleDias}.</span>
        <span id="pr-dash-revisar" style="margin-left:auto;font-weight:var(--sx-weight-semibold);cursor:pointer;text-decoration:underline">Revisar →</span>
      </div>`;
  }

  private static readonly ESTADO_PILL_CLS: Record<string, string> = {
    borrador: "borrador", en_revision: "am", aprobado: "blue", activo: "ok", suspendido: "red", cerrado: "na",
  };

  private _proyectosTablaHtml(): string {
    const recientes = [...this._proyectos]
      .sort((a, b) => Number(b.createdAt) - Number(a.createdAt))
      .slice(0, 8);
    if (recientes.length === 0) return "";
    return `
      <div class="pr-panel-t" style="margin-top:18px">Proyectos recientes</div>
      <div class="pr-tbl-wrap">
        <table class="pr-tbl">
          <thead><tr><th>Cliente</th><th>Cultivo</th><th>Ha</th><th>Técnico</th><th>Avance</th><th>CAPEX·OPEX</th><th>Estado</th><th>Creado</th></tr></thead>
          <tbody>
            ${recientes.map((p) => {
              const base = p.capex + p.opexAnual;
              const pct = base > 0 ? Math.min(100, Math.round((p.ejecutado / base) * 100)) : 0;
              const desv = base > 0 ? (p.ejecutado - base) / base : 0;
              const col = desv > 0.2 ? "var(--sx-error)" : desv > 0.1 ? "var(--sx-warning)" : "var(--sx-success)";
              return `<tr data-proyecto-id="${p.id}" style="cursor:pointer">
                <td><div style="font-weight:var(--sx-weight-semibold)">${this._esc(p.clienteNombre)}</div><div style="font-size:var(--sx-text-xs);color:var(--sx-text-tertiary)">${this._esc(p.nombre)}</div></td>
                <td><span class="pr-cultivo-emoji" aria-hidden="true">${cultivoEmoji(p.cultivoNombre)}</span>${this._esc(p.cultivoNombre)}</td>
                <td class="pr-num">${formatKg(p.ha)}</td>
                <td>${this._esc(p.tecnicoNombre)}</td>
                <td><span class="pr-prog-wrap"><span class="pr-prog-bar" style="width:${pct}%;background:${col}"></span></span> ${pct}%</td>
                <td class="pr-num">${fmtCentavos(p.capex)} · ${fmtCentavos(p.opexAnual)}</td>
                <td><span class="pr-pill ${ProyectosDashboardPage.ESTADO_PILL_CLS[p.estado] ?? "borrador"}">${ESTADO_LABELS[p.estado] ?? p.estado}</span></td>
                <td style="font-size:var(--sx-text-xs);color:var(--sx-text-tertiary)">${fmtFechaProy(p.createdAt)}</td>
              </tr>`;
            }).join("")}
          </tbody>
        </table>
      </div>`;
  }

  private _donutHtml(s: DashboardStatsResponse): string {
    const total = s.proyectosPorEstado.reduce((a, e) => a + e.cantidad, 0);
    const activos = s.proyectosPorEstado.find((e) => e.estado === "activo")?.cantidad ?? 0;
    const pctActivos = total > 0 ? Math.round((activos / total) * 100) : 0;
    const R = 36;
    const C = 2 * Math.PI * R;
    const entradas = ESTADO_ORDEN
      .map((estado) => s.proyectosPorEstado.find((e) => e.estado === estado))
      .filter((e): e is NonNullable<typeof e> => !!e && e.cantidad > 0);
    let offset = 0;
    const arcos = entradas.map((e) => {
      const len = (e.cantidad / total) * C;
      const circle = `<circle cx="45" cy="45" r="${R}" fill="none" stroke="${ESTADO_COLORS[e.estado] ?? "var(--sx-text-tertiary)"}" stroke-width="10" stroke-dasharray="${len.toFixed(1)} ${(C - len).toFixed(1)}" stroke-dashoffset="${(-offset).toFixed(1)}" stroke-linecap="round"/>`;
      offset += len;
      return circle;
    }).join("");
    const legend = entradas.map((e) => `
      <div class="pr-prog-row"><span class="pr-prog-dot" style="background:${ESTADO_COLORS[e.estado] ?? "var(--sx-text-tertiary)"}"></span>${ESTADO_LABELS[e.estado] ?? e.estado}<span class="pr-prog-val">${e.cantidad}</span></div>`).join("");
    return `
      <div class="pr-panel">
        <div class="pr-panel-t">Avance de portafolio</div>
        ${total === 0 ? `<div class="pr-empty">Sin datos</div>` : `
        <div class="pr-donut-row">
          <div class="pr-donut-wrap">
            <svg width="90" height="90" viewBox="0 0 90 90" style="transform:rotate(-90deg)">
              <circle cx="45" cy="45" r="${R}" fill="none" stroke="var(--sx-bg-subtle)" stroke-width="10"/>
              ${arcos}
            </svg>
            <div class="pr-donut-center"><span class="pr-donut-pct">${pctActivos}%</span><span class="pr-donut-lbl">activos</span></div>
          </div>
          <div class="pr-donut-legend">${legend}</div>
        </div>`}
      </div>`;
  }

  private _onExportar(): void {
    const s = this._stats;
    if (!s) return;
    const rows: (string | number)[][] = [
      ["Dashboard Proyectos", new Date().toLocaleDateString("es-CO")],
      [],
      ["KPI", "Valor"],
      ["Proyectos activos", s.proyectosActivos],
      ["Nuevos este mes", s.nuevosEsteMes],
      ["Hectáreas totales", s.totalHa],
      ["Técnicos activos", s.tecnicosActivos],
      ["Macro-Proyectos activos", s.macroProyectosActivos],
      ["Macro-Proyectos totales", s.totalMacroProyectos],
      ["Núcleos", s.totalNucleos],
      ["Beneficiarios", s.totalBeneficiarios],
      ["CAPEX presupuestado", s.capexPresupuestado],
      ["CAPEX ejecutado", s.capexEjecutado],
      ["OPEX presupuestado", s.opexPresupuestado],
      ["OPEX ejecutado", s.opexEjecutado],
      ["Proyectos pendientes de aprobación", s.proyectosPendientesAprobacion],
      [],
      ["Proyectos por estado", "Cantidad"],
      ...(s.proyectosPorEstado ?? []).map((e) => [ESTADO_LABELS[e.estado] ?? e.estado, e.cantidad]),
      [],
      ["Ha por cultivo", "Hectáreas"],
      ...(s.haPorCultivo ?? []).map((h) => [h.cultivoNombre, h.totalHa]),
    ];
    downloadCsv(`dashboard_proyectos_${new Date().toISOString().slice(0, 10)}.csv`, rows);
  }

  private _kpi(value: string, label: string, color: string, sub: string): string {
    return `<div class="pr-kcard">
      <div class="pr-k-val ${color}">${this._esc(value)}</div>
      <div class="pr-k-lbl">${label}</div>
      ${sub ? `<div class="pr-k-sub">${this._esc(sub)}</div>` : ""}
    </div>`;
  }

  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
}
